import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Lock, Video, Play, LogOut, Share2, MessageCircle, Facebook } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideosPage = () => {
  const navigate = useNavigate();
  const { logout, user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideosAndAccess();
  }, []);

  const fetchVideosAndAccess = async () => {
    try {
      // Check access
      const accessResponse = await axios.get(`${API}/videos/access`, {
        headers: getAuthHeaders()
      });
      setHasAccess(accessResponse.data.has_access);

      // Fetch videos
      const videosResponse = await axios.get(`${API}/videos`);
      setVideos(videosResponse.data.videos || []);
      
      if (videosResponse.data.videos?.length > 0 && accessResponse.data.has_access) {
        setSelectedVideo(videosResponse.data.videos[0]);
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    navigate("/checkout?type=videos");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate("/")} className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-12 h-12 text-orange-600" />
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Enseignements Vidéo
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Accédez à {videos.length} vidéo{videos.length > 1 ? 's' : ''} d'enseignement approfondi
            </p>

            <Card className="max-w-2xl mx-auto mb-8">
              <CardHeader className="bg-gradient-to-r from-orange-500 to-purple-600 text-white">
                <CardTitle className="text-2xl">Débloquer l'accès complet</CardTitle>
                <CardDescription className="text-white/90">
                  Un paiement unique pour un accès illimité à toutes les vidéos
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="text-center mb-6">
                  <p className="text-5xl font-bold text-orange-600 mb-2">3 USD</p>
                  <p className="text-gray-600">environ 6,000 CDF</p>
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      ✅ <strong>25 personnes</strong> ont déjà acheté l'accès aux vidéos
                    </p>
                  </div>
                </div>

                <div className="space-y-3 text-left mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">✓</span>
                    </div>
                    <span className="text-gray-700">Accès illimité à toutes les vidéos</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">✓</span>
                    </div>
                    <span className="text-gray-700">Regardez autant de fois que vous voulez</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">✓</span>
                    </div>
                    <span className="text-gray-700">Paiement Mobile Money sécurisé</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">✓</span>
                    </div>
                    <span className="text-gray-700">Accès immédiat après paiement</span>
                  </div>
                </div>

                <Button
                  onClick={handlePurchase}
                  size="lg"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg rounded-full"
                >
                  <Lock className="mr-2 h-5 w-5" />
                  Débloquer maintenant - 3 USD
                </Button>
              </CardContent>
            </Card>

            {/* Video previews */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {videos.slice(0, 4).map((video, index) => (
                <motion.div
                  key={video.video_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="relative rounded-lg overflow-hidden shadow-lg">
                    <img
                      src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_url}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full h-48 object-cover blur-sm"
                    />
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Lock className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  <h3 className="mt-3 font-semibold text-gray-900">{video.title}</h3>
                  {video.description && (
                    <p className="text-sm text-gray-600 mt-1">{video.description}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Has access - show videos
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/")}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Accueil
              </Button>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Video className="w-6 h-6 text-orange-600" />
                Enseignements Vidéo
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.phone}</span>
              <Button variant="outline" onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            {selectedVideo ? (
              <Card>
                <CardContent className="p-0">
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${selectedVideo.youtube_url}`}
                      title={selectedVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-t-lg"
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedVideo.title}
                    </h2>
                    {selectedVideo.description && (
                      <p className="text-gray-600 mb-4">{selectedVideo.description}</p>
                    )}
                    
                    {/* Share Buttons */}
                    <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                      <p className="w-full text-sm font-semibold text-gray-700 mb-2">📤 Partager cette vidéo:</p>
                      
                      {/* Facebook Share */}
                      <button
                        onClick={() => {
                          const url = window.location.href;
                          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                        }}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all hover:scale-105"
                      >
                        <Facebook className="w-4 h-4" />
                        <span className="text-sm font-semibold">Facebook</span>
                      </button>
                      
                      {/* WhatsApp Share */}
                      <button
                        onClick={() => {
                          const url = window.location.href;
                          const text = `Regardez cette vidéo: ${selectedVideo.title}`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                        }}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all hover:scale-105"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">WhatsApp</span>
                      </button>
                      
                      {/* Twitter/X Share */}
                      <button
                        onClick={() => {
                          const url = window.location.href;
                          const text = `Regardez: ${selectedVideo.title}`;
                          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                        }}
                        className="flex items-center gap-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-all hover:scale-105"
                      >
                        <Share2 className="w-4 h-4" />
                        <span className="text-sm font-semibold">Twitter</span>
                      </button>
                      
                      {/* Copy Link */}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                          alert('✅ Lien copié! Vous pouvez le partager maintenant.');
                        }}
                        className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-all hover:scale-105"
                      >
                        <Share2 className="w-4 h-4" />
                        <span className="text-sm font-semibold">Copier le lien</span>
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Sélectionnez une vidéo pour commencer</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Playlist */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Playlist ({videos.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {videos.map((video, index) => (
                    <button
                      key={video.video_id}
                      onClick={() => setSelectedVideo(video)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                        selectedVideo?.video_id === video.video_id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="relative flex-shrink-0">
                          <img
                            src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_url}/maxresdefault.jpg`}
                            alt={video.title}
                            className="w-24 h-16 object-cover rounded"
                          />
                          {selectedVideo?.video_id === video.video_id && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded">
                              <Play className="w-6 h-6 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">
                            {index + 1}. {video.title}
                          </p>
                          {video.description && (
                            <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                              {video.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideosPage;
